import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  useUpdateResourceUnit,
  useReplaceResource,
} from "@features/onboarding/utility-setup/hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ArrowLeft } from "lucide-react";

import { ModeSelectionStep } from "./ModeSelectionStep";
import { ResourceSelectionStep } from "./ResourceSelectionStep";
import { ResourceSummaryStep } from "./ResourceSummaryStep";

export interface InventoryResource {
  id: string;
  resourceId: string;
  service: string;
  type: string;
  subType: string;
  serialNo: string;
  manufacturer: string;
  model: string;
  capacity: string;
  location: string;
  status: "Available" | "In Use";
}

export interface ActiveResource {
  id: string;
  meterId: string;
  service: string;
  serialNo: string;
  type: string;
  subType?: string;
  lastReading: string;
  readDate: string;
  installDate: string;
}

export interface DisconnectedResource {
  id: string;
  meterId: string;
  service: string;
  serialNo: string;
  type: string;
  lastReading: string;
  installDate: string;
  disconnectedDate: string;
}

export type ModalMode = "none" | "add_new" | "update_existing";
export type ModalStep =
  | "mode_select"
  | "select_existing"
  | "form_select"
  | "summary";

interface AddResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeResources: ActiveResource[];
  onAttachResource: (newResource: InventoryResource) => void;
  onUpdateResource: (
    oldResource: ActiveResource,
    newResource: InventoryResource,
  ) => void;
}

export const AddResourceModal: React.FC<AddResourceModalProps> = ({
  isOpen,
  onClose,
  activeResources,
  onAttachResource,
  onUpdateResource,
}) => {
  const { code } = useParams<{ code: string }>();
  const decodedUnitCode = decodeURIComponent(code || "");
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const premise =
    searchParams.get("premise") ||
    location.state?.premise ||
    location.state?.entityData?.premise ||
    "Midwest Region#1_United States#1_Iowa#1";

  const updateResourceUnitMutation = useUpdateResourceUnit();
  const replaceResourceMutation = useReplaceResource();

  const [mode, setMode] = useState<ModalMode>("none");
  const [step, setStep] = useState<ModalStep>("mode_select");
  const [replacingResource, setReplacingResource] =
    useState<ActiveResource | null>(null);
  const [selectedNewResource, setSelectedNewResource] =
    useState<InventoryResource | null>(null);

  console.log("replacingResource", replacingResource);
  console.log("selectedNewResource", selectedNewResource);

  // Reset internal state when modal opens or closes
  useEffect(() => {
    if (!isOpen) {
      setMode("none");
      setStep("mode_select");
      setReplacingResource(null);
      setSelectedNewResource(null);
    }
  }, [isOpen]);

  const handleSelectMode = (selectedMode: ModalMode) => {
    setMode(selectedMode);
    if (selectedMode === "add_new") {
      setStep("form_select");
    } else if (selectedMode === "update_existing") {
      setStep("select_existing");
    }
  };

  const handleSelectReplacingResource = (resource: ActiveResource | null) => {
    setReplacingResource(resource);
    if (resource) {
      setStep("form_select");
    } else {
      setStep("select_existing");
    }
  };

  const handleSelectNewResource = (resource: InventoryResource) => {
    setSelectedNewResource(resource);
    setStep("summary");
  };

  const handleChooseDifferentOption = () => {
    setMode("none");
    setStep("mode_select");
    setReplacingResource(null);
    setSelectedNewResource(null);
  };

  const handleBackFromSummary = () => {
    if (mode === "add_new") {
      setStep("form_select");
      setSelectedNewResource(null);
    } else if (mode === "update_existing") {
      setStep("form_select");
      setSelectedNewResource(null);
    }
  };

  const handleConfirmAction = () => {
    switch (mode) {
      case "add_new":
        if (selectedNewResource) {
          updateResourceUnitMutation.mutate({
            resourceId:
              selectedNewResource.id || selectedNewResource.resourceId,
            payload: {
              unit_code: decodedUnitCode || "N/A",
              premise: premise,
            },
          });
          onAttachResource(selectedNewResource);
        }
        break;

      case "update_existing":
        if (replacingResource && selectedNewResource) {
          replaceResourceMutation.mutate({
            old_resource_id: Number(replacingResource.id),
            new_resource_id: Number(selectedNewResource.id),
          });
          onUpdateResource(replacingResource, selectedNewResource);
        }
        break;

      default:
        break;
    }
    onClose();
  };

  // Build Header Breadcrumb Title
  const renderHeaderTitle = () => {
    if (mode === "none" || step === "mode_select") {
      return <span>Add Resource</span>;
    }

    if (mode === "update_existing") {
      return (
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          <span className="text-gray-900 font-bold">Add Resource</span>
          <span className="text-gray-400 font-normal">/</span>
          <span className="text-gray-500 font-medium">Update Existing</span>
          {step === "summary" && (
            <>
              <span className="text-gray-400 font-normal">/</span>
              <span className="text-gray-500 font-medium">Summary</span>
            </>
          )}
        </span>
      );
    }

    if (mode === "add_new") {
      return (
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          <span className="text-gray-900 font-bold">Add Resource</span>
          <span className="text-gray-400 font-normal">/</span>
          <span className="text-gray-500 font-medium">Add New</span>
          {selectedNewResource && (
            <>
              <span className="text-gray-400 font-normal">/</span>
              <span className="text-blue-600 font-medium">
                {selectedNewResource.service}
              </span>
              <span className="text-gray-400 font-normal">/</span>
              <span className="text-blue-600 font-medium">
                {selectedNewResource.type}
              </span>
              <span className="text-gray-400 font-normal">/</span>
              <span className="text-blue-600 font-medium">
                {selectedNewResource.subType}
              </span>
            </>
          )}
        </span>
      );
    }

    return <span>Add Resource</span>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0 rounded-2xl overflow-hidden shadow-xl border border-gray-200/80 bg-white">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <DialogTitle className="text-base text-gray-900">
            {renderHeaderTitle()}
          </DialogTitle>
        </DialogHeader>

        {/* Modal Body / Steps */}
        <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
          {step === "mode_select" && (
            <ModeSelectionStep
              connectedCount={activeResources.length}
              onSelectMode={handleSelectMode}
            />
          )}

          {(step === "select_existing" || step === "form_select") && (
            <ResourceSelectionStep
              mode={mode}
              activeResources={activeResources}
              replacingResource={replacingResource}
              onSelectReplacingResource={handleSelectReplacingResource}
              onSelectNewResource={handleSelectNewResource}
            />
          )}

          {step === "summary" && (
            <ResourceSummaryStep
              mode={mode}
              newResource={selectedNewResource}
              replacingResource={replacingResource}
              onBack={handleBackFromSummary}
              onConfirm={handleConfirmAction}
            />
          )}
        </div>

        {/* Footer: "Choose different option" link (visible when in any mode) */}
        {mode !== "none" && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <button
              type="button"
              onClick={handleChooseDifferentOption}
              className="text-xs font-semibold text-gray-500 hover:text-gray-800 flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Choose different option
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddResourceModal;
